/**
 * CTAFinal — section CTA dark immersive du profil Particulier.
 *
 * Pattern : container rounded-[32px] bg-ink avec :
 *  - mesh-dark + halos accent/brand
 *  - grid pattern subtil avec mask radial
 *  - texture noise très douce
 *  - contenu : badge (pulse accent), titre gradient (mode dark), description,
 *    2 CTAs (brand + dark-outline), bullets de réassurance optionnelles
 *  - layout : center ou split (avec side slot pour LiveCard dark)
 */

import type { ReactNode } from 'react';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Container } from '../../layout/Container';
import { Button } from '../../ui/button';
import { Reveal } from '../../motion/Reveal';
import { getIcon } from '../../../lib/lucide-icons';
import { cn } from '../../../lib/cn';
import {
  PARTICULIER_COPY,
  PARTICULIER_GRADIENT,
  PARTICULIER_HALOS,
  type ParticulierCta,
} from '../../../config/audience/particulier.ui';
import { ParticulierEyebrow } from './Eyebrow';

export interface ParticulierCTAFinalProps {
  /** Badge en haut (par défaut : ctaFinalBadge) */
  badge?: string;
  /** Titre H2 — partie ivory */
  title: string;
  /** Titre H2 — partie gradient dark (optionnelle) */
  titleAccent?: string;
  /** Description */
  description?: ReactNode;
  /** CTA primaire (brand emerald sur fond noir) */
  primaryCta: ParticulierCta;
  /** CTA secondaire (dark-outline) */
  secondaryCta?: ParticulierCta;
  /** Bullets de réassurance (optionnel) */
  bullets?: string[];
  /** Footnote sous les bullets (optionnel) */
  footnote?: string;
  /** Slot droit (typiquement une ParticulierLiveCard variant=dark) */
  sideSlot?: ReactNode;
  /** Layout: center (par défaut) ou split (avec sideSlot) */
  layout?: 'center' | 'split';
  /** Surcharger les classes de section */
  sectionClass?: string;
}

export function ParticulierCTAFinal({
  badge = PARTICULIER_COPY.ctaFinalBadge,
  title,
  titleAccent,
  description,
  primaryCta,
  secondaryCta,
  bullets,
  footnote,
  sideSlot,
  layout = 'center',
  sectionClass,
}: ParticulierCTAFinalProps) {
  const PrimaryIcon = primaryCta.iconName ? getIcon(primaryCta.iconName) : null;
  const SecondaryIcon = secondaryCta?.iconName ? getIcon(secondaryCta.iconName) : null;

  const isSplit = layout === 'split' && Boolean(sideSlot);

  return (
    <section className={cn('relative bg-paper pb-20 pt-4', sectionClass)}>
      <Container>
        <div className="relative overflow-hidden rounded-[32px] bg-ink px-6 py-14 sm:px-12 sm:py-16 md:px-16 md:py-20">
          <div aria-hidden className="pointer-events-none absolute inset-0 bg-mesh-dark opacity-90" />
          <div aria-hidden className={PARTICULIER_HALOS.ctaTopRight} />
          <div aria-hidden className={PARTICULIER_HALOS.ctaBottomLeft} />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
              backgroundSize: '48px 48px',
              maskImage:
                'radial-gradient(ellipse 80% 60% at 50% 50%, black 30%, transparent 80%)',
              WebkitMaskImage:
                'radial-gradient(ellipse 80% 60% at 50% 50%, black 30%, transparent 80%)',
            }}
          />
          <div aria-hidden className="pointer-events-none absolute inset-0 bg-noise opacity-[0.04]" />

          <Reveal>
            <div
              className={cn(
                'relative',
                isSplit
                  ? 'grid items-center gap-10 lg:grid-cols-[1.4fr_1fr] lg:gap-16'
                  : 'text-center',
              )}
            >
              <div className={isSplit ? '' : ''}>
                <ParticulierEyebrow variant="dark" withDot>
                  {badge}
                </ParticulierEyebrow>

                <h2 className="mt-5 font-display text-[clamp(1.75rem,3.5vw,2.75rem)] font-bold leading-[1.05] tracking-tight text-balance text-ivory-50">
                  {title}
                  {titleAccent && (
                    <>
                      {' '}
                      <span className={PARTICULIER_GRADIENT.headlineDark}>{titleAccent}</span>
                    </>
                  )}
                </h2>

                {description && (
                  <div
                    className={cn(
                      'mt-4 text-[15px] leading-relaxed text-ivory-50/70',
                      isSplit ? 'max-w-xl' : 'mx-auto max-w-xl',
                    )}
                  >
                    {description}
                  </div>
                )}

                <div
                  className={cn(
                    'mt-7 flex flex-wrap gap-3',
                    isSplit ? '' : 'justify-center',
                  )}
                >
                  <Button asChild size="lg" variant="brand">
                    <a href={primaryCta.href}>
                      {PrimaryIcon && <PrimaryIcon className="h-4 w-4" strokeWidth={2} />}
                      {primaryCta.label}
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </Button>
                  {secondaryCta && (
                    <Button asChild size="lg" variant="dark-outline">
                      <a href={secondaryCta.href}>
                        {SecondaryIcon && <SecondaryIcon className="h-4 w-4" strokeWidth={2} />}
                        {secondaryCta.label}
                      </a>
                    </Button>
                  )}
                </div>

                {bullets && bullets.length > 0 && (
                  <div
                    className={cn(
                      'mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-ivory-50/60',
                      isSplit ? '' : 'mx-auto max-w-md justify-center',
                    )}
                  >
                    {bullets.map((b) => (
                      <span key={b} className="inline-flex items-center gap-1.5">
                        <CheckCircle2 className="h-3 w-3 text-accent-300/80" strokeWidth={2.2} />
                        {b}
                      </span>
                    ))}
                  </div>
                )}

                {footnote && (
                  <p className="mt-3 text-[11px] text-ivory-50/50">{footnote}</p>
                )}
              </div>

              {isSplit && sideSlot && <div className="hidden lg:block">{sideSlot}</div>}
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}
